import { useCallback, useEffect, useMemo, useState } from 'react';
import Page from '../../../../components/page/Page';
import Header from '../../../../components/operations/receptions/Header';
import { Box, IconButton, Paper, Tab, Tabs } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import useThemeMediaQuery from '../../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../../core/router/navigation';
import { useMeCore } from '../../../../../core/modules/me';
import { useGlobalCore } from '../../../../../core/modules/global';
import CreateDialog from '../../../../components/operations/receptions/CreateDialog';
import UpdateReceptionDialog from '../../../../components/operations/receptions/UpdateDialog';
import UpdateOrderDialog from '../../../../components/operations/receptionOrders/UpdateDialog';
import { useCompanyCore } from '../../../../../core/modules/company';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { CREATE_RECEPTION_DIALOG, UPDATE_RECEPTION_DIALOG, UPDATE_RECEPTION_ORDER_DIALOG } from '../../../../../core/constants/dialog';
import { useReceptionCore } from '../../../../../core/modules/reception';
import { Reception } from '../../../../../core/interfaces/reception.interface';
import moment from 'moment';
import { useMasterCore } from '../../../../../core/modules/master';
import { DOCUMENT_TYPE_LABEL } from '../../../../../core/constants/documentTypes';
import InventoryIcon from '@mui/icons-material/Inventory';
import { ReceptionOrder } from '../../../../../core/interfaces/receptionOrder.interface';
import { useBusinessCore } from '../../../../../core/modules/business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Swal from 'sweetalert2';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const Receptions = () => {
  const { user } = useMeCore();
  const { openDialog } = useGlobalCore();
  const { companyId } = useCompanyCore();
  const { receptions, receptionOrders, setReception, setReceptionOrder, getReceptions, getReceptionOrders, deleteReception, deleteReceptionOrder } = useReceptionCore();
  const { businesses, getBusinesses } = useBusinessCore();
  const { goToWelcome, goToReceptionContainers } = useNavigationCore();
  const { getTypes } = useMasterCore();

  const [tab, setTab] = useState(0);
  const [order, setOrder] = useState<ReceptionOrder | null>(null);
  const [isReceptionsLoading, setIsReceptionsLoading] = useState(true);
  const [isReceptionOrdersLoading, setIsReceptionOrdersLoading] = useState(true);
  const [receptionSortModel, setReceptionSortModel] = useState<GridSortModel>([{ field: 'received_at', sort: 'desc' }]);
  const [orderSortModel, setOrderSortModel] = useState<GridSortModel>([{ field: 'reception_date', sort: 'desc' }]);
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  // Manejar el error de ResizeObserver
  useEffect(() => {
    const handleResizeObserverError = () => {
      // Silenciar el error de ResizeObserver
      const originalError = console.error;
      console.error = (...args) => {
        if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
          return;
        }
        originalError.apply(console, args);
      };
    };

    handleResizeObserverError();
  }, []);

  useEffect(() => {
    if (
      !user ||
      (
        !user.privileges.superAdmin &&
        !user.privileges.readReception &&
        !user.privileges.writeReception
      )
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  useEffect(() => {
    if (!companyId) {
      setIsReceptionsLoading(false);
      setIsReceptionOrdersLoading(false);
    }
  }, [companyId]);

  const fetchReceptions = useCallback(() => {
    if (!companyId) return;
    setIsReceptionsLoading(true);
    getReceptions(
      companyId,
      undefined,
      () => setIsReceptionsLoading(false),
      () => setIsReceptionsLoading(false)
    );
  }, [companyId, getReceptions]);

  const fetchReceptionOrders = useCallback(() => {
    if (!companyId) return;
    setIsReceptionOrdersLoading(true);
    getReceptionOrders(
      companyId,
      undefined,
      () => setIsReceptionOrdersLoading(false),
      () => setIsReceptionOrdersLoading(false)
    );
  }, [companyId, getReceptionOrders]);

  useEffect(() => {
    if (companyId) {
      fetchReceptions();
      fetchReceptionOrders();
      getBusinesses(companyId);
    }
  }, [companyId, fetchReceptions, fetchReceptionOrders, getBusinesses]);

  const clpFormatter = useMemo(
    () =>
      new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    []
  );

  const businessById = useMemo(() => {
    const map = new Map<number, any>();
    businesses.forEach((business) => {
      map.set(business.id, business);
    });
    return map;
  }, [businesses]);

  const areaById = useMemo(() => {
    const map = new Map<number, any>();

    businesses.forEach((business) => {
      business.areas?.forEach((area) => {
        map.set(area.id, {
          ...area,
          businessId: business.id,
          businessName: business.name,
        });
      });
    });

    return map;
  }, [businesses]);

  const enhancedReceptionOrders = useMemo(() => {
    return receptionOrders.map((orderItem) => {
      const provider = businessById.get(orderItem.business_id);
      const area = areaById.get(orderItem.business_area_id);

      const providerName = provider?.name || 'Sin proveedor';
      const areaAcronym = area?.acronym || '';
      const areaName = area?.name || '';

      // Solo mostrar ID y Sigla Area
      const labelParts = [`#${orderItem.id}`];
      if (areaAcronym) {
        labelParts.push(areaAcronym);
      }

      return {
        ...orderItem,
        providerName,
        providerArea: areaAcronym || '-',
        providerAreaName: areaName,
        receptionOrderLabel: labelParts.join(' - '),
        has_receptions: orderItem.has_receptions ?? false, // Asegurar que siempre tenga un valor
      };
    });
  }, [receptionOrders, businessById, areaById]);

  const orderDetailsMap = useMemo(() => {
    const map = new Map<number, any>();
    enhancedReceptionOrders.forEach((orderItem) => {
      map.set(orderItem.id, orderItem);
    });
    return map;
  }, [enhancedReceptionOrders]);

  const enhancedReceptions = useMemo(() => {
    return receptions.map((reception) => {
      const details = reception.reception_order_id
        ? orderDetailsMap.get(reception.reception_order_id)
        : undefined;

      return {
        ...reception,
        providerName: details?.providerName || '-',
        providerArea: details?.providerArea || '-',
        providerAreaName: details?.providerAreaName || '',
        receptionOrderLabel: details?.receptionOrderLabel || '-',
      };
    });
  }, [receptions, orderDetailsMap]);

  const seeContainers = useCallback((reception: Reception) => {
    setReception(reception);
    goToReceptionContainers(reception.id);
  }, [goToReceptionContainers, setReception]);

  const deleteReceptionHandler = useCallback((reception: Reception) => {
    if (companyId) {
      Swal.fire({
        title: '¿Estás seguro?',
        text: `¿Estás seguro de que quieres eliminar la recepción ${reception.document_number}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          deleteReception(companyId, reception.id, () => {
            // Recargar la lista después de eliminar
            fetchReceptions();
            // Recargar órdenes de recepción para actualizar has_receptions
            fetchReceptionOrders();
            // Mostrar mensaje de éxito
            Swal.fire(
              '¡Eliminado!',
              'La recepción ha sido eliminada correctamente.',
              'success'
            );
          }, () => {
            // En caso de error
            Swal.fire(
              'Error',
              'No se pudo eliminar la recepción. Inténtalo de nuevo.',
              'error'
            );
          });
        }
      });
    }
  }, [companyId, deleteReception, fetchReceptions, fetchReceptionOrders]);

  const selectReception = useCallback((reception: Reception) => {
    setReception(reception);
    if (companyId) {
      getTypes(companyId, 'reception');
      fetchReceptionOrders();
    }
    openDialog(UPDATE_RECEPTION_DIALOG);
  }, [companyId, fetchReceptionOrders, getTypes, openDialog, setReception]);

  const receiveOrder = useCallback((order: ReceptionOrder) => {
    setOrder(order);
    if (companyId) {
      getTypes(companyId, 'reception');
      fetchReceptionOrders();
    }
    openDialog(CREATE_RECEPTION_DIALOG);
  }, [companyId, fetchReceptionOrders, getTypes, openDialog]);

  const selectOrder = useCallback((order: ReceptionOrder) => {
    setReceptionOrder(order);
    if (companyId) {
      getTypes(companyId, 'acquisition');
      getBusinesses(companyId);
    }
    openDialog(UPDATE_RECEPTION_ORDER_DIALOG);
  }, [companyId, getTypes, openDialog, getBusinesses, setReceptionOrder]);

  const deleteReceptionOrderHandler = useCallback((order: ReceptionOrder) => {
    if (companyId) {
      Swal.fire({
        title: '¿Estás seguro?',
        text: `¿Estás seguro de que quieres eliminar la orden de recepción ${order.document_number}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        reverseButtons: true
      }).then((result) => {
        if (result.isConfirmed) {
          deleteReceptionOrder(companyId, order.id, () => {
            // Recargar la lista después de eliminar
            fetchReceptionOrders();
            // Mostrar mensaje de éxito
            Swal.fire(
              '¡Eliminado!',
              'La orden de recepción ha sido eliminada correctamente.',
              'success'
            );
          }, () => {
            // En caso de error
            Swal.fire(
              'Error',
              'No se pudo eliminar la orden de recepción. Inténtalo de nuevo.',
              'error'
            );
          });
        }
      });
    }
  }, [companyId, deleteReceptionOrder, fetchReceptionOrders]);

  const receptionColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      cellClassName: 'flex items-center p-8',
      width: 80,
    },
    {
      field: 'received_at',
      headerName: 'Fecha de Recepción',
      cellClassName: 'flex items-center p-8',
      type: 'number',
      sortable: true,
      valueGetter: (params: any) => {
        const value = params?.row?.received_at;
        // Retornar el timestamp como número para ordenamiento correcto
        // Si no hay valor, retornar 0 para que aparezcan al final
        return value ? Number(value) : 0;
      },
      sortComparator: (v1, v2) => {
        // Comparar directamente los valores numéricos
        const num1 = Number(v1) || 0;
        const num2 = Number(v2) || 0;
        return num1 - num2;
      },
      sortingOrder: ['desc', 'asc', null],
      renderCell: (params: GridRenderCellParams) => (
        <div>
          {params.row.received_at
            ? moment.unix(params.row.received_at).format('DD/MM/YYYY')
            : '-'}
        </div>
      ),
      flex: 0.9,
    },
    {
      field: 'receptionOrderLabel',
      headerName: 'Orden de ingreso',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.receptionOrderLabel || '-'}</div>
      ),
      flex: 1.4,
    },
    {
      field: 'providerName',
      headerName: 'Proveedor',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.providerName || '-'}</div>
      ),
      flex: 1.2,
    },
    {
      field: 'providerArea',
      headerName: 'Área',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.providerArea || '-'}</div>
      ),
      flex: 0.8,
    },
    {
      field: 'document_type_id',
      headerName: 'Tipo de Documento',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{DOCUMENT_TYPE_LABEL[params.row.document_type_id] || '-'}</div>
      ),
      flex: 1,
    },
    {
      field: 'document_number',
      headerName: 'Número de Documento',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.document_number || '-'}</div>
      ),
      flex: 1,
    },
    {
      field: 'quantity',
      headerName: 'Cantidad',
      cellClassName: 'flex items-center p-8',
      type: 'number',
      flex: 0.7,
    },
    {
      field: 'notes',
      headerName: 'Observación',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.notes || '-'}</div>
      ),
      flex: 1,
    },
    {
      field: 'actions',
      headerName: '',
      cellClassName: 'flex items-center justify-end p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div className="flex gap-4">
          <IconButton
            aria-label="see"
            onClick={() => seeContainers(params.row)}
            color="primary"
            size="small"
          >
            <InventoryIcon />
          </IconButton>
          <IconButton
            aria-label="delete"
            onClick={() => deleteReceptionHandler(params.row)}
            color="error"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  const orderColumns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      cellClassName: 'flex items-center p-8',
      width: 80,
    },
    {
      field: 'reception_date',
      headerName: 'Fecha de Recepción',
      cellClassName: 'flex items-center p-8',
      type: 'number',
      sortable: true,
      valueGetter: (params: any) => {
        const value = params?.row?.reception_date;
        // Retornar el timestamp como número para ordenamiento correcto
        // Si no hay valor, retornar 0 para que aparezcan al final
        return value ? Number(value) : 0;
      },
      sortComparator: (v1, v2) => {
        // Comparar directamente los valores numéricos
        const num1 = Number(v1) || 0;
        const num2 = Number(v2) || 0;
        return num1 - num2;
      },
      sortingOrder: ['desc', 'asc', null],
      renderCell: (params: GridRenderCellParams) => (
        <div>
          {params.row.reception_date
            ? moment.unix(params.row.reception_date).format('DD/MM/YYYY')
            : '-'}
        </div>
      ),
      flex: 0.9,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.description || '-'}</div>
      ),
      flex: 1.2,
    },
    {
      field: 'providerName',
      headerName: 'Proveedor',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.providerName || '-'}</div>
      ),
      flex: 1.2,
    },
    {
      field: 'providerArea',
      headerName: 'Área',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.providerArea || '-'}</div>
      ),
      flex: 0.8,
    },
    {
      field: 'document_type_id',
      headerName: 'Tipo de Documento',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{DOCUMENT_TYPE_LABEL[params.row.document_type_id] || '-'}</div>
      ),
      flex: 1,
    },
    {
      field: 'document_number',
      headerName: 'Número de Documento',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div>{params.row.document_number || '-'}</div>
      ),
      flex: 1,
    },
    {
      field: 'cost',
      headerName: 'Costo',
      cellClassName: 'flex items-center p-8',
      valueGetter: (params: any) => params?.row?.cost ?? 0,
      renderCell: (params: GridRenderCellParams) => (
        <div>
          {params.row.cost && params.row.cost > 0
            ? clpFormatter.format(params.row.cost)
            : '-'}
        </div>
      ),
      flex: 1,
    },
    {
      field: 'commission',
      headerName: 'Comisión (%)',
      cellClassName: 'flex items-center p-8',
      renderCell: (params: GridRenderCellParams) => (
        <p>
          {params.row.commission && params.row.commission > 0
            ? `${params.row.commission}%`
            : '-'}
        </p>
      ),
      width: 140,
    },
    {
      field: 'actions',
      headerName: '',
      cellClassName: 'flex items-center justify-end p-8',
      renderCell: (params: GridRenderCellParams) => (
        <div className="flex gap-2">
          <IconButton
            aria-label="receive"
            onClick={() => receiveOrder(params.row)}
            size="small"
          >
            <LocalShippingIcon />
          </IconButton>
         {/*  <IconButton
            aria-label="edit"
            onClick={() => selectOrder(params.row)}
            size="small"
          >
            <EditIcon />
          </IconButton> */}
          <IconButton
            aria-label="delete"
            onClick={() => deleteReceptionOrderHandler(params.row)}
            size="small"
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ),
      width: 160,
    },
  ];

  const filteredOrders = useMemo(() => {
    if (!enhancedReceptionOrders || enhancedReceptionOrders.length === 0) {
      return [];
    }
    return enhancedReceptionOrders.filter(order => {
      // Filtrar órdenes que no tienen recepciones o que has_receptions es undefined/null
      return order.has_receptions === false || order.has_receptions === undefined || order.has_receptions === null;
    });
  }, [enhancedReceptionOrders]);

  return (
    <>
      <Page
        header={<Header />}
        content={
          <div className="w-full my-16 px-24 md:px-32">
            <Paper className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden">
              <div className="w-full p-32">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-col md:flex-row items-center justify-between gap-8">
                  <Tabs
                    value={tab}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="Recepciones" {...a11yProps(0)} />
                    <Tab label="Ordenes" {...a11yProps(1)} />
                  </Tabs>
                </Box>
                <CustomTabPanel value={tab} index={0}>
                  <DataGrid
                    rows={enhancedReceptions}
                    columns={receptionColumns}
                    sx={{ border: 0 }}
                    sortModel={receptionSortModel}
                    onSortModelChange={(model) => setReceptionSortModel(model)}
                    sortingMode="client"
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    getRowHeight={() => 'auto'}
                    pagination={undefined}
                    loading={isReceptionsLoading}
                  />
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={1}>
                  <DataGrid
                    rows={filteredOrders}
                    columns={orderColumns}
                    sx={{ border: 0 }}
                    sortModel={orderSortModel}
                    onSortModelChange={(model) => setOrderSortModel(model)}
                    sortingMode="client"
                    disableColumnFilter
                    disableRowSelectionOnClick
                    disableMultipleRowSelection
                    hideFooter
                    getRowHeight={() => 52}
                    pagination={undefined}
                    loading={isReceptionOrdersLoading}
                  />
                </CustomTabPanel>
              </div>
            </Paper>
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <CreateDialog order={order} onSuccess={() => setTab(0)} onClose={() => setOrder(null)} />
      <UpdateReceptionDialog />
      <UpdateOrderDialog />
    </>
  );
};

export default Receptions;
